import { useState } from "react";
import { spot as spotCore, type AutopilotCycleSummary, type AutopilotView } from "@auto-pools/core";
import { AutopilotCandidate } from "../components/AutopilotCandidate";
import { AutopilotCloseDialog } from "../components/AutopilotCloseDialog";
import { AutopilotJournal } from "../components/AutopilotJournal";
import { AutopilotLimitsCard } from "../components/AutopilotLimitsCard";
import { AutopilotPauseDialog } from "../components/AutopilotPauseDialog";
import { AutopilotStartDialog } from "../components/AutopilotStartDialog";
import { AutopilotStrategy } from "../components/AutopilotStrategy";
import { useAutopilot } from "../components/autopilotContext";
import { useViewer } from "../auth/authContext";
import { spotCardStatus } from "../components/spot/spotPresentation";
import { rulesChangedSinceStart, useAutopilotResume } from "../components/useAutopilotResume";

type Tab = "overview" | "strategy";

/**
 * The close dialog is bound to the position the operator saw when opening it, not to the live
 * cycle: if the held position changes underneath the open dialog, Confirm must still name the
 * original id so the server refuses `position_changed` instead of closing the replacement.
 */
type Dialog =
  | { kind: "start" | "pause" }
  | {
      kind: "close";
      positionId: string;
      pair: string;
      exitNow: AutopilotCycleSummary["exitNow"];
    };

interface LifecycleAction {
  kind: "resume" | "start" | "pause" | "close";
  label: string;
}

/**
 * The pill's own button stays Start/Pause/Open (design 47, section 1); "Open" is exactly how it
 * reaches this page for paused, limit and needs-attention. Once here, the operator still needs a
 * way to act (review D1): Pause while limit or needs-attention, since the server still runs its
 * exit duties and accepts Pause in both (`apps/server/src/autopilot/commands.ts` `pauseRun`).
 *
 * While paused, a restored approval can only continue through a brand-new Start: `resumeRun`
 * refuses it outright ("A restored run needs a new Start"). Every *other* pause — an ordinary
 * operator pause, or one issued right after `needs-attention` lost the wallet, password, or rules
 * approval — stores the identical `hold.reason: "operator"` (`pauseRun` never records why the run
 * was running before the pause), so the view genuinely cannot tell them apart afterward. Resuming
 * a lost-authority run only re-arms the same stale approval and loops straight back to
 * `needs-attention` (`resumeRun` does not reseal it — only `startRun` does). So this offers Resume
 * *and*, whenever the server's own start gate (`view.start`, `apps/server/src/autopilot/status.ts`
 * `startGate`) says a Start would actually be accepted right now, a new Start alongside it — never
 * a guess at "lost authority" the view doesn't expose, just the same allowed/refused signal the
 * Start dialog itself already gates on.
 */
/**
 * The one held cycle this toolbar's single, unambiguous "Close position" action can target.
 * With more than one position held, each gets its own close/take-control affordance on its own
 * card (My positions, `PositionCard.tsx`) instead — this button only ever means "the" position.
 */
function onlyHeldCycle(status: AutopilotView["status"]): AutopilotCycleSummary | null {
  return status.cycles.length === 1 ? status.cycles[0] : null;
}

/**
 * With the server's combined `controls` (design 54): Resume whenever either engine is resumable
 * (the shared resume resumes only those, so a spot-only pause resumes without touching running
 * pools); Pause and Start only where the pill's one button does not already carry them (it shows
 * Pause first, and routes pools in limit/needs-attention here). The pools-only refinements stay: a restored
 * pools run continues only through a new Start, a paused one may also start anew when the server's
 * start gate allows it, and Close position for the one held position.
 */
function combinedLifecycleActions(
  view: AutopilotView,
  controls: NonNullable<AutopilotView["controls"]>,
): LifecycleAction[] {
  const { status, start } = view;
  const poolsPause = status.state === "paused" && status.reason?.state === "paused";
  const restored = status.reason?.state === "paused" && status.reason.pause === "restored";
  const poolsAttention = status.state === "limit" || status.state === "needs-attention";
  const actions: LifecycleAction[] = [];
  if (controls.resume) actions.push({ kind: "resume", label: "Resume" });
  const cycle = onlyHeldCycle(status);
  if (poolsPause && cycle?.closeFromPause.allowed && cycle.positionId)
    actions.push({ kind: "close", label: "Close position" });
  if ((controls.start && controls.pause) || restored || (poolsPause && start.allowed))
    actions.push({ kind: "start", label: "Start autopilot" });
  if (controls.pause && poolsAttention) actions.push({ kind: "pause", label: "Pause" });
  return actions;
}

function pageLifecycleActions(view: AutopilotView): LifecycleAction[] {
  if (view.controls) return combinedLifecycleActions(view, view.controls);
  const { status, start } = view;
  if (status.state === "paused" && status.reason?.state === "paused") {
    if (status.reason.pause === "restored") return [{ kind: "start", label: "Start autopilot" }];
    const actions: LifecycleAction[] = rulesChangedSinceStart(view.rules)
      ? []
      : [{ kind: "resume", label: "Resume" }];
    // Only while the operator's own predicate agrees the one held position is closable right now
    // (`cycle.closeFromPause`, `apps/server/src/autopilot/commands.ts` `closeFromPauseRefusal`);
    // `positionId` is re-checked defensively since the type still allows a flat cycle.
    const cycle = onlyHeldCycle(status);
    if (cycle?.closeFromPause.allowed && cycle.positionId)
      actions.push({ kind: "close", label: "Close position" });
    if (start.allowed) actions.push({ kind: "start", label: "Start autopilot" });
    return actions;
  }
  if (status.state === "limit" || status.state === "needs-attention")
    return [{ kind: "pause", label: "Pause" }];
  return [];
}

/** A coarse, page-level summary only (design 54) — the pill and the status strip already carry
 * the detailed, cycle-aware wording; this row exists so two engines are never read as one. */
function poolsStateLabel(status: AutopilotView["status"]): string {
  if (status.hold === "vault-locked") return "Vault locked";
  if (status.hold === "recovering") return "Recovering";
  switch (status.state) {
    case "off":
      return "Off";
    case "paused":
      return "Paused";
    case "stopping":
      return "Stopping…";
    case "limit":
      return "Stopped · limit";
    case "needs-attention":
      return "Needs attention";
    case "running":
      return status.cycles.length > 0
        ? `In position${status.cycles.length > 1 ? "s" : ""}`
        : "Running";
  }
}

/** The two-engine summary (design 54): one row each, never merged into a single status. */
function AutopilotEngineRows({ view }: { view: AutopilotView }) {
  if (!view.spot) return null;
  const poolsTone = spotCore.poolsTone(view.status);
  const spot = spotCardStatus(view.spot);
  return (
    <div className="autopilot-engine-rows">
      <div className={`autopilot-engine-row ${poolsTone}`}>
        <span className={`status-dot ${poolsTone}`} aria-hidden="true" />
        <b>Pools · Orca</b>
        <span>{poolsStateLabel(view.status)}</span>
      </div>
      <div className={`autopilot-engine-row ${spot.tone}`}>
        <span className={`status-dot ${spot.tone}`} aria-hidden="true" />
        <b>Spot · Bybit</b>
        <span>{spot.label}</span>
      </div>
    </div>
  );
}

function AutopilotTabs({ tab, onTab }: { tab: Tab; onTab: (tab: Tab) => void }) {
  const tabs: Tab[] = ["overview", "strategy"];
  return (
    <div role="tablist" aria-label="Autopilot views" className="history-tabs autopilot-tabs">
      {tabs.map((value) => (
        <button
          key={value}
          id={`autopilot-${value}-tab`}
          role="tab"
          aria-selected={tab === value}
          aria-controls={`autopilot-${value}`}
          tabIndex={tab === value ? 0 : -1}
          onClick={() => {
            onTab(value);
          }}
        >
          {value === "overview" ? "Overview" : "Strategy"}
        </button>
      ))}
    </div>
  );
}

export function AutopilotPage({
  onOpenPosition,
}: {
  /** Navigates to the position/operation the journal or candidate points at. */
  onOpenPosition: (target: { operationId?: string; positionId?: string }) => void;
}) {
  const [tab, setTab] = useState<Tab>("overview");
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const autopilot = useAutopilot();
  const view = autopilot.data;
  const { resume, resuming, error: resumeError } = useAutopilotResume();
  const viewer = useViewer();
  // Every lifecycle action is a command: a viewer gets none of them, and so none of the dialogs.
  const actions = view && !viewer ? pageLifecycleActions(view) : [];
  const onlyCycle = view ? onlyHeldCycle(view.status) : null;

  return (
    <div className="autopilot-page">
      <header className="positions-toolbar">
        <div className="positions-context">
          <strong>Autopilot</strong>
          {view && (
            <span>
              Rules v{view.rules.version}
              {rulesChangedSinceStart(view.rules) && " · rules changed since Start"}
            </span>
          )}
        </div>
        <AutopilotTabs tab={tab} onTab={setTab} />
        {actions.length > 0 && (
          <div className="positions-toolbar-actions">
            {actions.map((action) => (
              <button
                key={action.kind}
                type="button"
                className={action.kind === "start" ? "button" : "button primary"}
                disabled={action.kind === "resume" && resuming}
                onClick={() => {
                  if (action.kind === "resume") void resume();
                  else if (action.kind !== "close") setDialog({ kind: action.kind });
                  else if (onlyCycle?.positionId)
                    setDialog({
                      kind: "close",
                      positionId: onlyCycle.positionId,
                      pair: onlyCycle.pair,
                      exitNow: onlyCycle.exitNow,
                    });
                }}
              >
                {action.kind === "resume" && resuming ? "Resuming…" : action.label}
              </button>
            ))}
          </div>
        )}
      </header>
      {autopilot.error && (
        <p className="inline-alert error" role="alert">
          {autopilot.error.message}
        </p>
      )}
      {resumeError && (
        <p className="inline-alert warning" role="alert">
          {resumeError}
        </p>
      )}
      {tab === "overview" ? (
        <section role="tabpanel" id="autopilot-overview" aria-labelledby="autopilot-overview-tab">
          {!view ? (
            <p role="status">Loading autopilot status…</p>
          ) : (
            <>
              <AutopilotEngineRows view={view} />
              <AutopilotCandidate candidate={view.candidate} />
              <AutopilotLimitsCard status={view.status} />
              <AutopilotJournal onOpen={onOpenPosition} />
            </>
          )}
        </section>
      ) : (
        <section role="tabpanel" id="autopilot-strategy" aria-labelledby="autopilot-strategy-tab">
          <AutopilotStrategy />
        </section>
      )}
      {dialog?.kind === "start" && view && (
        <AutopilotStartDialog
          view={view}
          onClose={() => {
            setDialog(null);
          }}
          onStarted={() => {
            setDialog(null);
            autopilot.reload();
          }}
        />
      )}
      {dialog?.kind === "pause" && (
        <AutopilotPauseDialog
          view={view}
          onClose={() => {
            setDialog(null);
          }}
          onPaused={() => {
            setDialog(null);
            autopilot.reload();
          }}
        />
      )}
      {dialog?.kind === "close" && (
        <AutopilotCloseDialog
          pair={dialog.pair}
          positionId={dialog.positionId}
          // A fresher valuation is shown only while it still belongs to the captured position.
          exitNow={onlyCycle?.positionId === dialog.positionId ? onlyCycle.exitNow : dialog.exitNow}
          onClose={() => {
            setDialog(null);
          }}
          onClosed={() => {
            setDialog(null);
            autopilot.reload();
          }}
        />
      )}
    </div>
  );
}
